/**
 * @file     formatPhone.ts
 * @readme   ./README.md
 * @purpose  Phone input formatting and E.164 normalization for CRM delivery.
 */

/**
 * Formats a raw phone input into (XXX) XXX-XXXX as the user types, keeping a
 * country code visible as a "+1 " prefix when one is given.
 *
 * A North American area code can never begin with 1, so a leading 1 is always
 * the country code. Treating it as an area-code digit shifted every following
 * digit one place left and pushed the last one past the ten-digit cap, so
 * "1 250 880 0326" rendered as "(125) 088-0326" and lost a digit outright.
 */
export function formatPhoneInput(raw: string): string {
  const all = raw.replace(/\D/g, '');
  const hasCountryCode = all.length > 1 && all.startsWith('1');
  const digits = (hasCountryCode ? all.slice(1) : all).slice(0, 10);
  const prefix = hasCountryCode ? '+1 ' : '';

  if (digits.length === 0) return hasCountryCode ? '+1 ' : all;
  if (digits.length < 4) return `${prefix}${digits}`;
  if (digits.length < 7) return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Normalizes a phone number to E.164 (+1XXXXXXXXXX) for the CRM, which is the
 * form Follow Up Boss matches and dials on. Returns null when the input is not
 * a complete North American number, so a half-typed value is never sent as if
 * it were real.
 *
 * Display formatting is for the person filling the form; what reaches the CRM
 * should be canonical, so the same human is not stored twice under two
 * spellings of one number.
 */
export function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const all = raw.replace(/\D/g, '');
  const digits = all.length === 11 && all.startsWith('1') ? all.slice(1) : all;
  if (digits.length !== 10) return null;
  // NANP: neither the area code nor the exchange may begin with 0 or 1.
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
  return `+1${digits}`;
}
